import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, FileText, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, inr, Money } from "@/lib/api";
import { can, useAuth } from "@/context/AuthContext";
import { DataTable, DateRangeFilter, DemoBadge, EntityCombobox, Field, PageHeader, SearchBox, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["Enquiry", "Quotation", "Confirmed", "Partially Paid", "Fully Paid", "Travel Completed", "Cancelled", "Refunded"];
const r2 = (x) => Math.round((Number(x) + Number.EPSILON) * 100) / 100;

function BookingForm({ open, onClose, onSaved, editItem }) {
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const empty = { customer_id: "", agent_id: "", service_id: "", package_name: "", destination: "",
    start_date: "", end_date: "", adults: 2, children: 0, pickup: "", drop: "", vehicle: "",
    driver: "", hotel: "", room_type: "", meal_plan: "", package_cost: 0, discount: 0,
    gst_rate: 5, advance: 0, status: "Enquiry", notes: "" };
  const [form, setForm] = useState(empty);
  useEffect(() => {
    if (!open) return;
    setForm(editItem ? { ...empty, ...editItem } : empty);
    Promise.all([api.get("/customers?limit=200"), api.get("/agents?limit=200"), api.get("/services?active_only=true&limit=200")])
      .then(([c, a, s]) => { setCustomers(c.data.items); setAgents(a.data.items); setServices(s.data.items); })
      .catch(() => {});
  }, [open, editItem]);

  const taxable = r2(Number(form.package_cost || 0) - Number(form.discount || 0));
  const gst = r2(taxable * Number(form.gst_rate || 0) / 100);
  const total = r2(taxable + gst);

  const onService = (sid) => {
    const s = services.find((x) => x.id === sid);
    if (s) setForm((f) => ({ ...f, service_id: sid, package_name: s.name,
      destination: s.name.replace(" Tour Package", ""), package_cost: s.price, gst_rate: s.gst_rate }));
  };

  const submit = async () => {
    if (!form.customer_id) return toast.error("Select a customer");
    setSaving(true);
    try {
      if (editItem) await api.put(`/bookings/${editItem.id}`, form);
      else await api.post("/bookings", form);
      toast.success("Booking saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="booking-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{editItem ? "Edit" : "New"} Booking</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="col-span-2"><Field label="Customer" required>
            <EntityCombobox testid="booking-customer" value={form.customer_id} onChange={(v) => setForm({ ...form, customer_id: v })}
              items={customers.map((c) => ({ value: c.id, label: c.name, sub: `${c.phone}${c.is_demo ? " · DEMO" : ""}` }))} placeholder="Select customer…" /></Field></div>
          <Field label="B2B Agent">
            <EntityCombobox testid="booking-agent" value={form.agent_id} onChange={(v) => setForm({ ...form, agent_id: v })}
              items={[{ value: "", label: "— Direct —" }, ...agents.map((a) => ({ value: a.id, label: a.agency_name, sub: a.agent_id }))]} placeholder="Direct / agent…" /></Field>
          <div className="col-span-2"><Field label="Package (from catalog)">
            <EntityCombobox testid="booking-package" value={form.service_id} onChange={onService}
              items={services.map((s) => ({ value: s.id, label: s.name, sub: `${inr(s.price)} · GST ${s.gst_rate}%` }))} placeholder="Select package…" /></Field></div>
          <Field label="Package Name" required><Input value={form.package_name} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, package_name: e.target.value })} data-testid="booking-package-name" /></Field>
          <Field label="Destination"><Input value={form.destination} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="booking-destination" /></Field>
          <Field label="Start Date" required><Input type="date" value={form.start_date} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="booking-start-date" /></Field>
          <Field label="End Date"><Input type="date" value={form.end_date} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="booking-end-date" /></Field>
          <Field label="Adults"><Input type="number" min="0" value={form.adults} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, adults: Number(e.target.value) })} data-testid="booking-adults" /></Field>
          <Field label="Children"><Input type="number" min="0" value={form.children} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, children: Number(e.target.value) })} data-testid="booking-children" /></Field>
          <Field label="Pickup"><Input value={form.pickup} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, pickup: e.target.value })} data-testid="booking-pickup" /></Field>
          <Field label="Drop"><Input value={form.drop} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, drop: e.target.value })} data-testid="booking-drop" /></Field>
          <Field label="Vehicle"><Input value={form.vehicle} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, vehicle: e.target.value })} data-testid="booking-vehicle" /></Field>
          <Field label="Driver"><Input value={form.driver} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, driver: e.target.value })} data-testid="booking-driver" /></Field>
          <Field label="Hotel / Stay"><Input value={form.hotel} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, hotel: e.target.value })} data-testid="booking-hotel" /></Field>
          <Field label="Room Type"><Input value={form.room_type} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, room_type: e.target.value })} data-testid="booking-room-type" /></Field>
          <Field label="Meal Plan"><Input value={form.meal_plan} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, meal_plan: e.target.value })} data-testid="booking-meal-plan" /></Field>
          <Field label="Package Cost (₹)" required><Input type="number" min="0" value={form.package_cost}
            className="bg-white border-[#E5E0D8]" onChange={(e) => setForm({ ...form, package_cost: Number(e.target.value) })}
            data-testid="booking-package-cost" /></Field>
          <Field label="Discount (₹)"><Input type="number" min="0" value={form.discount} className="bg-white border-[#E5E0D8]"
            onChange={(e) => setForm({ ...form, discount: Number(e.target.value) })} data-testid="booking-discount" /></Field>
          <Field label="GST %"><Select value={String(form.gst_rate)} onValueChange={(v) => setForm({ ...form, gst_rate: Number(v) })}>
            <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="booking-gst-rate"><SelectValue /></SelectTrigger>
            <SelectContent>{[0, 5, 12, 18, 28].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
          </Select></Field>
          {!editItem && (
            <Field label="Advance Received (₹)"><Input type="number" min="0" value={form.advance}
              className="bg-white border-[#E5E0D8]" onChange={(e) => setForm({ ...form, advance: Number(e.target.value) })}
              data-testid="booking-advance" /></Field>
          )}
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="bg-white border-[#E5E0D8]" data-testid="booking-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select></Field>
          <div className="col-span-2 sm:col-span-3"><Field label="Notes">
            <Textarea value={form.notes} className="bg-white border-[#E5E0D8]"
              onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="booking-notes" /></Field></div>
        </div>
        <div className="bg-[#FAF8F5] border border-[#E5E0D8] rounded-lg p-3 text-sm flex flex-wrap gap-x-6" data-testid="booking-totals-preview">
          <span>Taxable: <b className="num">{inr(taxable)}</b></span>
          <span>GST: <b className="num">{inr(gst)}</b></span>
          <span>Total: <b className="num text-[#0D4F4A]">{inr(total)}</b></span>
          {!editItem && <span>Balance: <b className="num text-red-600">{inr(r2(total - Number(form.advance || 0)))}</b></span>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0D4F4A] hover:bg-[#136A63]" data-testid="booking-submit">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BookingDetail({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const load = useCallback(() => {
    if (id) api.get(`/bookings/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  useEffect(load, [load]);
  const b = data?.booking;
  const setStatus = async (status) => {
    try {
      await api.post(`/bookings/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const saveCosts = async (costs) => {
    try {
      await api.put(`/bookings/${id}`, { ...b, costs });
      toast.success("Costs updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!id} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-dialog">
        {!b ? <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-3">
                {b.booking_id} <StatusBadge value={b.status} testid="booking-detail-status" />
                {b.is_demo && <DemoBadge />}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm bg-[#FAF8F5] border border-[#E5E0D8] rounded-lg p-4">
              <div><span className="text-[#5C6B69]">Customer:</span> <b>{b.customer_name}</b></div>
              <div><span className="text-[#5C6B69]">Agent:</span> {b.agent_name || "Direct"}</div>
              <div><span className="text-[#5C6B69]">Package:</span> {b.package_name}</div>
              <div><span className="text-[#5C6B69]">Destination:</span> {b.destination}</div>
              <div><span className="text-[#5C6B69]">Travel:</span> {fmtDate(b.start_date)} → {fmtDate(b.end_date)}</div>
              <div><span className="text-[#5C6B69]">Travellers:</span> {b.travellers} ({b.adults}A + {b.children}C)</div>
              <div><span className="text-[#5C6B69]">Vehicle:</span> {b.vehicle || "—"} {b.driver ? `· ${b.driver}` : ""}</div>
              <div><span className="text-[#5C6B69]">Hotel:</span> {b.hotel || "—"} {b.room_type ? `(${b.room_type})` : ""}</div>
              <div><span className="text-[#5C6B69]">Payment:</span> {b.payment_status}</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
              <div className="bg-white border border-[#E5E0D8] rounded-lg p-3"><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Total</div><span className="num font-bold text-[#0D4F4A]">{inr(b.total)}</span></div>
              <div className="bg-white border border-[#E5E0D8] rounded-lg p-3"><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Advance</div><span className="num font-bold text-emerald-600">{inr(b.advance)}</span></div>
              <div className="bg-white border border-[#E5E0D8] rounded-lg p-3"><div className="text-[10px] uppercase font-bold text-[#5C6B69]">Balance</div><span className="num font-bold text-red-600">{inr(b.balance)}</span></div>
              <div className="bg-white border border-[#E5E0D8] rounded-lg p-3">
                <div className="text-[10px] uppercase font-bold text-[#5C6B69]">Est. Profit</div>
                <span className="num font-bold text-[#0D4F4A]">{inr(r2(b.taxable - Object.values(b.costs || {}).reduce((s, v) => s + Number(v || 0), 0)))}</span>
              </div>
            </div>
            {can(user?.role, "bookings", true) && (
              <div className="mt-4 space-y-2">
                <div className="text-xs font-bold uppercase tracking-wide text-[#5C6B69]">Estimated Costs (for profitability)</div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {["hotel", "transport", "driver", "activities", "other"].map((k) => (
                    <div key={k}>
                      <label className="text-[10px] uppercase font-semibold text-[#5C6B69]">{k}</label>
                      <Input type="number" min="0" defaultValue={b.costs?.[k] || 0} className="h-9 bg-white border-[#E5E0D8]"
                        data-testid={`booking-cost-${k}`}
                        onBlur={(e) => saveCosts({ ...(b.costs || {}), [k]: Number(e.target.value) })} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-[#5C6B69] mb-2">Invoices ({data.invoices.length})</div>
                {data.invoices.map((i) => (
                  <button key={i.id} onClick={() => navigate(`/invoices/${i.id}`)}
                    className="w-full text-left text-sm flex justify-between border border-[#E5E0D8] rounded-lg px-3 py-2 mb-1.5 hover:bg-[#FAF8F5]">
                    <span className="font-semibold text-[#0D4F4A]">{i.invoice_no || "DRAFT"}</span>
                    <span className="num">{inr(i.grand_total)}</span>
                  </button>
                ))}
                {data.invoices.length === 0 && <p className="text-xs text-[#5C6B69] italic">No invoices yet.</p>}
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-[#5C6B69] mb-2">Payments ({data.payments.length})</div>
                {data.payments.map((p) => (
                  <div key={p.id} className="text-sm flex justify-between border border-[#E5E0D8] rounded-lg px-3 py-2 mb-1.5">
                    <span className="font-semibold text-[#0D4F4A]">{p.receipt_no}</span>
                    <span className="num">{inr(p.amount)}</span>
                  </div>
                ))}
                {data.payments.length === 0 && <p className="text-xs text-[#5C6B69] italic">No payments yet.</p>}
              </div>
            </div>
            {can(user?.role, "bookings", true) && (
              <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-[#E5E0D8]">
                <Select value={b.status} onValueChange={setStatus}>
                  <SelectTrigger className="w-44 bg-white border-[#E5E0D8]" data-testid="booking-detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                {can(user?.role, "invoices", true) && (
                  <Button size="sm" className="bg-[#0D4F4A] hover:bg-[#136A63]"
                    onClick={() => navigate(`/invoices/new?booking=${b.id}&customer=${b.customer_id}`)}
                    data-testid="booking-create-invoice"><FileText className="h-3.5 w-3.5 mr-1" /> Create Invoice</Button>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Bookings() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailId, setDetailId] = useState(params.get("focus") || null);
  const writable = can(user?.role, "bookings", true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/bookings", { params: { q, status, page, limit: 25, from_date: range.from, to_date: range.to } });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setLoading(false);
  }, [q, status, page, range]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (params.get("new") === "1" && writable) { setEditItem(null); setFormOpen(true); }
  }, [params, writable]);

  return (
    <div>
      <PageHeader title="Bookings" subtitle="Travel bookings pipeline — enquiry to completion"
        testid="bookings-header"
        actions={
          <>
            <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search booking ID, customer, destination…" testid="bookings-search" />
            <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-40 bg-white border-[#E5E0D8]" data-testid="bookings-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <DateRangeFilter from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} testid="bookings-date-filter" />
            {writable && (
              <Button className="bg-[#0D4F4A] hover:bg-[#136A63]" onClick={() => { setEditItem(null); setFormOpen(true); }}
                data-testid="bookings-create-button"><Plus className="h-4 w-4 mr-1" /> New Booking</Button>
            )}
          </>
        } />
      <DataTable testid="bookings-table" loading={loading}
        columns={[
          { key: "booking_id", label: "ID", render: (r) => <span className="font-semibold text-[#0D4F4A]">{r.booking_id}{r.is_demo && <DemoBadge />}</span> },
          { key: "customer_name", label: "Customer", render: (r) => (
            <div><div className="font-medium">{r.customer_name}</div>
              {r.agent_name && <div className="text-xs text-[#5C6B69]">via {r.agent_name}</div>}</div>) },
          { key: "package_name", label: "Package / Destination", render: (r) => (
            <div><div>{r.package_name}</div><div className="text-xs text-[#5C6B69]">{r.destination}</div></div>) },
          { key: "start_date", label: "Travel", render: (r) => <span className="text-xs">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span> },
          { key: "travellers", label: "Pax", align: "right" },
          { key: "total", label: "Total", align: "right", render: (r) => <Money value={r.total} className="font-semibold" /> },
          { key: "balance", label: "Balance", align: "right", render: (r) => (
            <span className={`num ${r.balance > 0 ? "text-red-600" : "text-emerald-600"}`}>{r.balance > 0 ? inr(r.balance) : "—"}</span>) },
          { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} testid={`booking-status-${r.id}`} /> },
        ]}
        rows={data.items} page={data.page} pages={data.pages} total={data.total} onPage={setPage}
        onRow={(r) => setDetailId(r.id)} empty="No bookings yet." />
      <div className="mt-3 text-xs text-[#5C6B69] flex items-center gap-1">
        <Eye className="h-3.5 w-3.5" /> Click a row for full details, invoices, payments & profitability costs.
        {editItem && <button className="text-[#0D4F4A] font-semibold flex items-center gap-1" onClick={() => setEditItem(null)}><X className="h-3 w-3" /> Clear edit</button>}
      </div>
      <BookingForm open={formOpen} onClose={() => { setFormOpen(false); setEditItem(null); }} onSaved={load} editItem={editItem} />
      <BookingDetail id={detailId} onClose={() => setDetailId(null)} onChanged={load} />
      {detailId && writable && (
        <div className="fixed bottom-6 right-6 z-40">
          <Button variant="outline" className="shadow-lg bg-white" data-testid="booking-edit-from-detail"
            onClick={() => { const b = data.items.find((x) => x.id === detailId); if (b) { setEditItem(b); setFormOpen(true); } }}>
            Edit this booking
          </Button>
        </div>
      )}
    </div>
  );
}
